import React, { useMemo, useState } from "react";
import {
  useCloseDaySheet,
  useFetchCashDeposits,
  useFetchCenters,
  useFetchLastDaySheet,
  useFetchRemainingCash,
  useFetchTransactionRecords,
} from "store/backend/assets";
import {
  Dialog,
  ErrorMessage,
  PrimaryButton,
  SingleSelect,
  Table,
  Textfield,
} from "widgets";

import ErrorButton from "widgets/Buttons/ErrorButton";
import Loader from "shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import {
  CASH_DEPOSITS_COLUMN,
  ORDER_STATUSES,
  ORGANIZATIONS,
  POST_SUBMIT_RELOAD_DELAY_IN_MS,
  SUMMARY_OF_TRANSACTIONS_HEADERS,
  TRANSACTION_RECORDS_COLUMN,
} from "constants/constants";
import {
  formatReadableDate,
  formatReadableDateTime,
  fromISTDateTime,
  toDatetimeLocal,
  toISTDateTime,
} from "utils/formatDate";
import CashDenominationForm from "components/CashDenominationForm/CashDenominationForm";
import SecondaryButton from "widgets/Buttons/SecondaryButton";
import { formatINRCurrency } from "utils/formatINRCurrency";

import styles from "./DaySheetForm.module.scss";

const closeDaySheetPayloadBuilder = (values, transactionRecords) => {
  const cashDenominations = Object.entries(values.denominationDetails)
    .map(([key, count]) => {
      const denomination = parseInt(key.replace("count_", ""), 10);
      const countValue = parseInt(count, 10) || 0;
      return { denomination, count: countValue };
    })
    .filter((item) => item.count > 0);

  return {
    tenant_id: values.centerId,
    agent_id: values.agentId,
    cash_denominations: cashDenominations,
    current_day_sheet_close_time: values.endTime,
    total_cash_amount: transactionRecords.reduce(
      (acc, transaction) =>
        transaction.payment_details.payment_mode === "CASH"
          ? acc + transaction.tax_details.total_amount
          : acc,
      0
    ),
    total_cheque_amount: transactionRecords.reduce(
      (acc, transaction) =>
        transaction.payment_details.payment_mode === "CHEQUE"
          ? acc + transaction.tax_details.total_amount
          : acc,
      0
    ),
    taxable_amount: transactionRecords.reduce(
      (acc, transaction) => acc + transaction.tax_details.taxable_amount,
      0
    ),
  };
};

const handleOnClickConfirmSubmitDaySheetClosureButton = (
  values,
  closeDaySheet,
  openSnackbar,
  setDialogContent,
  transactionRecords
) => {
  const payload = closeDaySheetPayloadBuilder(values, transactionRecords);
  closeDaySheet(payload, {
    onSuccess: () => {
      openSnackbar("Successfully closed day sheet", "success");
      setTimeout(() => {
        window.location.reload();
      }, POST_SUBMIT_RELOAD_DELAY_IN_MS);
      setDialogContent(null);
    },
    onError: (error) => {
      openSnackbar(
        error?.message ||
          "An unexpected error occurred while processing the request. Please try again.",
        "error"
      );
    },
  });
};
const handleDialogClose = (setDialogContent) => () => {
  setDialogContent(null);
};
const handleOnClickSubmitButton = (setDialogContent) => {
  setDialogContent(true);
};

const handleChangeCashDenominations = (value, unitValue, setFieldValue) => {
  const fieldName = `denominationDetails.count_${unitValue}`;

  if (value === "") {
    setFieldValue(fieldName, "");
    return;
  }

  const count = parseInt(value);

  if (!isNaN(count)) {
    setFieldValue(fieldName, Math.max(0, count));
  }
};

const getOrganizationById = (id) =>
  ORGANIZATIONS.find((org) => org.id === id) ?? null;

const getOrderStatusById = (statusId) =>
  Object.keys(ORDER_STATUSES).find((key) => ORDER_STATUSES[key] === statusId);

const getTransactionRecordsData = (transactionRecords) => {
  if (!transactionRecords) return [];

  const sortedRecords = [...transactionRecords].sort((a, b) => {
    const dateA = new Date(a.payment_details.payment_collected_at);
    const dateB = new Date(b.payment_details.payment_collected_at);
    return dateB - dateA;
  });

  return sortedRecords.map((transaction) => ({
    applicationNumber: transaction.application_number,
    orderStatus: getOrderStatusById(transaction.order_status_id),
    organization: getOrganizationById(transaction.billing_entity_id)?.label,
    paymentHead: transaction.payment_head.name,
    paymentHeadCategory: transaction.payment_head.category.name,
    invoiceNo: transaction.tax_details.invoice_number,
    cgst: transaction.tax_details.cgst_amount,
    sgst: transaction.tax_details.sgst_amount,
    igst: transaction.tax_details.igst_amount,
    taxableAmount: transaction.tax_details.taxable_amount,
    totalAmount: transaction.tax_details.total_amount,
    paymentCreatedOn: formatReadableDateTime(
      transaction.payment_details.payment_created_at
    ),
    paymentCollectedOn: formatReadableDateTime(
      transaction.payment_details.payment_collected_at
    ),
    paymentMode: transaction.payment_details.payment_mode,
    chequeReferenceNo: transaction.payment_details.cheque_reference_number,
    chequeBankName: transaction.payment_details.cheque_bank_name,
    hsnSacCode: transaction.payment_head.hsn_sac_code,
  }));
};

const getSummaryOfTransactionsData = (transactionRecords) => {
  if (!transactionRecords) return [];
  return [
    {
      currentClosedDaySheetSummary:
        "Local Fee Collections (Inclusive of Taxes)",
      amount: formatINRCurrency(
        transactionRecords.reduce(
          (acc, transaction) => acc + transaction.tax_details.total_amount,
          0
        )
      ),
    },
    {
      currentClosedDaySheetSummary:
        "Local Fee Collections (Exclusive of Taxes)",
      amount: formatINRCurrency(
        transactionRecords.reduce(
          (acc, transaction) => acc + transaction.tax_details.taxable_amount,
          0
        )
      ),
    },
    {
      currentClosedDaySheetSummary: "Total Center Cash Payments",
      amount: formatINRCurrency(
        transactionRecords.reduce(
          (acc, transaction) =>
            transaction.payment_details.payment_mode === "CASH"
              ? acc + transaction.tax_details.total_amount
              : acc,
          0
        )
      ),
    },
    {
      currentClosedDaySheetSummary: "Total Center Cheque Payments",
      amount: formatINRCurrency(
        transactionRecords.reduce(
          (acc, transaction) =>
            transaction.payment_details.payment_mode === "CHEQUE"
              ? acc + transaction.tax_details.total_amount
              : acc,
          0
        )
      ),
    },
  ];
};

const getCashDepositsData = (cashDeposits) => {
  if (!cashDeposits) return [];
  return cashDeposits.map((cashDeposit) => ({
    organization: cashDeposit.billing_entity.name,
    headCashierName: cashDeposit.agent_details.name,
    headCashierPhoneNumber: cashDeposit.agent_details.phone,
    cashDepositedDate: formatReadableDate(cashDeposit.deposit_time),
    cashInHand: formatINRCurrency(
      cashDeposit.deposited_amount + cashDeposit.remaining_cash_in_hand
    ),
    cashDepositedAmount: formatINRCurrency(cashDeposit.deposited_amount),
    balanceAmount: formatINRCurrency(cashDeposit.remaining_cash_in_hand),
  }));
};

const DaySheetForm = (props) => {
  const { openSnackbar } = useSnackbarContext();
  const { values, setFieldValue, resetForm, isValid } = props;

  const { isPending: isFetchCentersLoading, data: centers } = useFetchCenters();

  const [appliedFilters, setAppliedFilters] = useState(null);

  const [dialogContent, setDialogContent] = useState(null);

  const [daySheetCenterId, setDaySheetCenterId] = useState(null);

  const {
    data: lastDaySheet,
    fetchStatus: fetchStatusLastDaySheet,
    isError: isErrorOnFetchingLastDaySheet,
    error: errorMessageOnFetchingLastDaySheet,
  } = useFetchLastDaySheet(daySheetCenterId);

  const {
    data: transactionRecords,
    fetchStatus: fetchStatusTransactionRecords,
    isError: isErrorOnFetchingTransactionRecords,
    error: errorMessageOnFetchingTransactionRecords,
  } = useFetchTransactionRecords(appliedFilters, !!appliedFilters);

  const {
    data: remainingCash,
    fetchStatus: fetchStatusRemainingCash,
    isError: isErrorOnFetchingRemainingCash,
    error: errorMessageOnFetchingRemainingCash,
  } = useFetchRemainingCash(appliedFilters, !!appliedFilters);

  const {
    data: cashDeposits,
    fetchStatus: fetchStatusCashDeposits,
    isError: isErrorOnFetchingCashDeposits,
    error: errorMessageOnFetchingCashDeposits,
  } = useFetchCashDeposits(appliedFilters, !!appliedFilters);

  const { mutate: closeDaySheet, isPending: isCloseDaySheetPending } =
    useCloseDaySheet();

  const isDateRangeInvalid = useMemo(() => {
    if (!lastDaySheet?.close_time || !values.endTime) return false;
    const fromDate = new Date(lastDaySheet.close_time);
    const toDate = new Date(values.endTime);
    const now = new Date();
    return toDate < fromDate || toDate > now;
  }, [lastDaySheet?.close_time, values.endTime]);

  const dateRangeError = useMemo(() => {
    if (!lastDaySheet?.close_time || !values.endTime) return "";

    const from = new Date(lastDaySheet.close_time);
    const to = new Date(values.endTime);
    const now = new Date();

    if (to < from)
      return "Current day sheet close time must be after last day sheet close time";
    if (to > now) return "Current day sheet close time cannot be in the future";

    return "";
  }, [lastDaySheet?.close_time, values.endTime]);

  if (fetchStatusLastDaySheet === "fetching") return <Loader />;

  if (isErrorOnFetchingTransactionRecords) {
    return (
      <ErrorMessage>
        {errorMessageOnFetchingTransactionRecords?.message ||
          "An error occurred while fetching transaction records."}
      </ErrorMessage>
    );
  }

  if (isErrorOnFetchingRemainingCash) {
    return (
      <ErrorMessage>
        {errorMessageOnFetchingRemainingCash?.message ||
          "An error occurred while fetching remaining cash."}
      </ErrorMessage>
    );
  }

  if (isErrorOnFetchingCashDeposits) {
    return (
      <ErrorMessage>
        {errorMessageOnFetchingCashDeposits?.message ||
          "An error occurred while fetching cash deposits."}
      </ErrorMessage>
    );
  }

  const CENTERS = Object.entries(centers || {}).map(([uuid, details]) => ({
    id: details?.id || uuid,
    value: details?.id || "",
    label: details?.name || "Unknown Center",
  }));

  const isApplied =
    appliedFilters?.centerId === values.centerId &&
    appliedFilters?.endTime === values.endTime;

  const denominationTotal = Object.entries(values.denominationDetails).reduce(
    (acc, [key, count]) => {
      const unit = parseInt(key.replace("count_", ""), 10);
      return acc + unit * (Number(count) || 0);
    },
    0
  );

  const sumOfCashDeposits =
    cashDeposits?.reduce(
      (acc, cashDeposit) => acc + (cashDeposit.deposited_amount || 0),
      0
    ) ?? 0;

  const sumOfRemainingCashInHand = remainingCash ?? 0;

  const isMathMismatch = denominationTotal !== sumOfRemainingCashInHand;

  const hasTransactionRecords =
    transactionRecords && transactionRecords?.length > 0;
  const hasCashDeposits = cashDeposits && cashDeposits?.length > 0;

  const transactionRecordsRows = getTransactionRecordsData(transactionRecords);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>Center</span>
            <SingleSelect
              tag="Center"
              value={values.centerId}
              menuItems={CENTERS}
              handleChange={(e) => {
                setFieldValue("centerId", e.target.value);
                setDaySheetCenterId(e.target.value);
              }}
              disabled={isFetchCentersLoading}
            />
          </div>

          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>
              Last Day Sheet Close Time
            </span>

            <Textfield
              type="datetime-local"
              value={toDatetimeLocal(lastDaySheet?.close_time)}
              disabled
              error={isErrorOnFetchingLastDaySheet}
              helperText={
                isErrorOnFetchingLastDaySheet
                  ? errorMessageOnFetchingLastDaySheet?.message ||
                    "An error occurred while fetching last day sheet."
                  : ""
              }
            />
          </div>

          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>
              Current Day Sheet Close Time
            </span>
            <Textfield
              type="datetime-local"
              value={fromISTDateTime(values.endTime)}
              onChange={(e) =>
                setFieldValue("endTime", toISTDateTime(e.target.value))
              }
              error={isDateRangeInvalid}
              helperText={dateRangeError}
              inputProps={{
                max: new Date().toISOString().slice(0, 16),
              }}
            />
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          <div className={styles.buttonsGroup}>
            <ErrorButton
              onClick={() => {
                setAppliedFilters(null);
                resetForm();
              }}
            >
              Clear filter
            </ErrorButton>

            <PrimaryButton
              disabled={
                isDateRangeInvalid ||
                isApplied ||
                !values.centerId ||
                !values.endTime
              }
              onClick={() =>
                setAppliedFilters({
                  centerId: values.centerId,
                  endTime: values.endTime,
                })
              }
            >
              {fetchStatusTransactionRecords === "fetching" ||
              fetchStatusCashDeposits === "fetching" ||
              fetchStatusRemainingCash === "fetching"
                ? "Fetching..."
                : isApplied
                ? "Applied"
                : "Apply filter"}
            </PrimaryButton>
          </div>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <h3 className={styles.sectionHeader}>Transaction Records</h3>
        <Table
          columns={TRANSACTION_RECORDS_COLUMN}
          rows={transactionRecordsRows}
          loading={fetchStatusTransactionRecords === "fetching"}
        />
      </div>
      <div className={styles.tableContainer}>
        <h3 className={styles.sectionHeader}>Summary of Transactions</h3>
        <Table
          columns={SUMMARY_OF_TRANSACTIONS_HEADERS}
          rows={getSummaryOfTransactionsData(transactionRecords)}
          loading={fetchStatusTransactionRecords === "fetching"}
        />
      </div>
      <div className={styles.tableContainer}>
        <h3 className={styles.sectionHeader}>Cash Deposits</h3>
        <Table
          columns={CASH_DEPOSITS_COLUMN}
          rows={getCashDepositsData(cashDeposits)}
          loading={fetchStatusCashDeposits === "fetching"}
        />
      </div>
      <div className={styles.tableContainer}>
        <h3 className={styles.sectionHeader}>Cash Deposit Denomination</h3>
        <CashDenominationForm
          values={values}
          amountDetails={[
            {
              label: "Total Center Deposited Amount",
              amount: sumOfCashDeposits,
            },
            {
              label: "Present Cash In Hand",
              amount: sumOfRemainingCashInHand,
            },
          ]}
          amountToValidate={sumOfRemainingCashInHand}
          amountLabel="Campus Cash In Hand Denomination Total"
          handleChangeCashDenominations={(value, unitValue) =>
            handleChangeCashDenominations(value, unitValue, setFieldValue)
          }
        />
      </div>

      <div className={styles.submitButton}>
        <PrimaryButton
          onClick={() => handleOnClickSubmitButton(setDialogContent)}
          disabled={
            !isValid ||
            isMathMismatch ||
            !hasTransactionRecords ||
            !hasCashDeposits
          }
        >
          Close Day Sheet
        </PrimaryButton>
      </div>
      <Dialog
        open={dialogContent !== null}
        handleClose={handleDialogClose(setDialogContent)}
      >
        <div className={styles.dialogContent}>
          <span className={styles.dialogMessage}>
            You’re about to submit these details. Once submitted, you won’t be
            able to edit or change this information. Please review everything
            carefully before proceeding.
          </span>
          <div className={styles.dialogActions}>
            <SecondaryButton onClick={handleDialogClose(setDialogContent)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() =>
                handleOnClickConfirmSubmitDaySheetClosureButton(
                  values,
                  closeDaySheet,
                  openSnackbar,
                  setDialogContent,
                  transactionRecords
                )
              }
            >
              {isCloseDaySheetPending ? "Submitting..." : "Submit"}
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DaySheetForm;
